import {
  describeBiometricHang,
  describeNoLocalAuthState,
} from './biometric-errors'
import type { BiometricAvailability } from './biometric-availability-probe'

export type SoftLockProbeDecision = {
  policyActive: boolean
  /** When true, lock immediately. When `evaluateClock`, caller uses the 12h TTL. */
  lock: boolean
  evaluateClock: boolean
  needsEnroll: boolean
  failed: boolean
  errorMessage: string | null
  optionalPrompt: boolean
}

/**
 * Map the (already time-boxed) availability probe onto soft-lock UI state.
 *
 * A hung/missing plugin used to skip the lock entirely so settings wouldn't
 * spin forever. Password fallback is always available on the sheet now, so
 * mandatory L3+ must stay locked with Try again + password — never an auth
 * bypass just because `isAvailable()` never returned.
 */
export function decideSoftLockAfterProbe(input: {
  mandatory: boolean
  optionalOffer: boolean
  unlockEnabled: boolean
  availability: BiometricAvailability
}): SoftLockProbeDecision {
  const { mandatory, optionalOffer, unlockEnabled, availability } = input
  const label = availability.label

  if (availability.probeFailed) {
    if (mandatory) {
      return {
        policyActive: true,
        lock: true,
        evaluateClock: false,
        needsEnroll: false,
        failed: true,
        errorMessage: describeBiometricHang(label),
        optionalPrompt: false,
      }
    }
    return {
      policyActive: false,
      lock: false,
      evaluateClock: false,
      needsEnroll: false,
      failed: false,
      errorMessage: null,
      optionalPrompt: false,
    }
  }

  if (mandatory) {
    if (!availability.available) {
      return {
        policyActive: true,
        lock: true,
        evaluateClock: false,
        needsEnroll: false,
        failed: true,
        errorMessage: describeNoLocalAuthState(label),
        optionalPrompt: false,
      }
    }
    if (!unlockEnabled) {
      return {
        policyActive: true,
        lock: true,
        evaluateClock: false,
        needsEnroll: true,
        failed: false,
        errorMessage: null,
        optionalPrompt: false,
      }
    }
    return {
      policyActive: true,
      lock: false,
      evaluateClock: true,
      needsEnroll: false,
      failed: false,
      errorMessage: null,
      optionalPrompt: false,
    }
  }

  if (unlockEnabled && availability.available) {
    return {
      policyActive: true,
      lock: false,
      evaluateClock: true,
      needsEnroll: false,
      failed: false,
      errorMessage: null,
      optionalPrompt: false,
    }
  }

  return {
    policyActive: false,
    lock: false,
    evaluateClock: false,
    needsEnroll: false,
    failed: false,
    errorMessage: null,
    optionalPrompt: optionalOffer && !unlockEnabled && availability.available,
  }
}
